import { getFragmentData } from 'gql'
import { AuthUserFragmentFragmentDoc, ErrorFragmentFragmentDoc } from 'gql/graphql'
import { isNil } from 'lodash'

import { getTokenCookies } from 'features/user'
import { authUserFromDTO } from 'features/user/api/authUser'
import { authenticate, reset } from 'features/user/store/userSlice'
import { store } from 'providers/redux'
import { request } from 'providers/tanstack-query/client'
import { handleResponse200 } from 'providers/tanstack-query/response'
import { getKey } from 'providers/tanstack-query/utils'
import { REFRESH_TOKEN } from './mutation'
import { removeTokenCookies } from './tokenCookies'

import type { RefreshTokenResult } from './types'
import type { RefreshTokenMutation, RefreshTokenMutationVariables } from 'gql/graphql'
import type { Ability } from 'providers/casl'
import type { GraphQlResponse } from 'providers/tanstack-query/response'

/**
 * Remember to catch Promise errors when using this function because it does not handle them internally.
 */
export const refreshToken = (ability: Ability): Promise<RefreshTokenResult> => {
  const { refreshToken } = getTokenCookies()

  if (isNil(refreshToken)) {
    store.dispatch(reset())

    return Promise.resolve(undefined)
  }

  return request<RefreshTokenMutationVariables, RefreshTokenMutation>({
    key: getKey(REFRESH_TOKEN),
    query: REFRESH_TOKEN,
    variables: {
      canAccessGamConnection: ability.can('access', 'GlobalSetupPage-googleAdManagerConnection'),
      refreshToken,
    },
  }).then((response: GraphQlResponse<RefreshTokenMutation> | void) => {
    const data: RefreshTokenMutation | undefined = handleResponse200<RefreshTokenMutation>({
      response,
    })

    if (data === undefined) {
      return undefined
    }

    const accessToken = data.refreshToken?.accessToken
    const refreshToken = data.refreshToken?.refreshToken
    const errors = getFragmentData(ErrorFragmentFragmentDoc, data.refreshToken?.errors)
    const user = getFragmentData(AuthUserFragmentFragmentDoc, data.refreshToken?.user)

    const hasErrors: boolean = !isNil(errors) && errors.length > 0

    if (isNil(accessToken) || isNil(refreshToken) || isNil(user) || hasErrors) {
      removeTokenCookies()
      window.location.reload()
      return undefined
    }

    store.dispatch(
      authenticate({
        accessToken,
        refreshToken,
        user: authUserFromDTO(user),
      }),
    )

    return { accessToken, refreshToken }
  })
}
